/**
 * @file
 * @fileOverview This file contains the SocketIOClient class for handling Socket.IO client operations.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

import io from 'socket.io-client';

/**
 * @class SocketIOClient
 * @classdesc A class to handle Socket.IO client operations with detailed error handling.
 */
class SocketIOClient {
  /**
   * Creates an instance of SocketIOClient.
   * @param {string} url - The URL of the Socket.IO server.
   * @param {Object} [options={}] - Optional configuration options for the Socket.IO client.
   * @param {function} [onConnect=null] - Optional callback for the connect event.
   * @param {function} [onDisconnect=null] - Optional callback for the disconnect event.
   * @param {function} [onConnectError=null] - Optional callback for the connect_error event.
   * @param {function} [onError=null] - Optional callback for the error event.
   * @param {function} [onReconnectError=null] - Optional callback for the reconnect_error event.
   */
  constructor(
    url,
    options = {},
    onConnect = null,
    onDisconnect = null,
    onConnectError = null,
    onError = null,
    onReconnect_error = null
  ) {
    // Set the default transports if not provided
    const defaultOptions = {
      transports: ['websocket'],
      rejectUnauthorized: false, // Disables certificate validation
      secure: true, // Ensures that SSL/TLS is used
    };

    // Merge default options with user-provided options
    this.options = {
      ...defaultOptions,
      ...options,
    };

    // Initialize the socket with the URL and options
    this.socket = io(url, this.options);

    if (onConnect == null) {
      this.socket.on('connect', () => {
        console.log('Connected to socket server');
      });
    } else {
      this.socket.on('connect', onConnect.bind(this));
    }

    if (onDisconnect == null) {
      this.socket.on('disconnect', () => {
        console.log('Disconnected from socket server');
      });
    } else {
      this.socket.on('disconnect', onDisconnect.bind(this));
    }

    if (onConnectError == null) {
      this.socket.on('connect_error', (err) => {
        console.error('Connection error:', err);
      });
    } else {
      this.socket.on('connect_error', onConnectError.bind(this));
    }

    this.socket.on('connect_timeout', (timeout) => {
      console.error('Connection timeout:', timeout);
    });

    if (onError == null) {
      this.socket.on('error', (err) => {
        console.error('Socket error:', err);
      });
    } else {
      this.socket.on('error', onError.bind(this));
    }

    if (onReconnect_error == null) {
      this.socket.on('reconnect_error', (err) => {
        console.error('Reconnection error:', err);
      });
    } else {
      this.socket.on('reconnect_error', onReconnect_error.bind(this));
    }

    this.socket.on('reconnect_failed', () => {
      console.error('Reconnection failed');
    });

    this.joinRoom(this.generateUniqueString());
  }

  /**
   * Sends data to the server with multiple arguments.
   * @param {string} event - The event name.
   * @param {any} data - The data to send.
   * @param {...any} additionalArguments - Additional arguments to send.
   */
  sendMultipleData(event, data, ...additionalArguments) {
    this.socket.emit(event, data, ...additionalArguments);
  }

  /**
   * Sends data to the server.
   * @param {string} event - The event name.
   * @param {any} data - The data to send.
   */
  sendData(event, data) {
    this.socket.emit(event, data);
  }

  joinRoom(room) {
    this.socket.emit('join', { room: room });
  }

  leaveRoom(room) {
    this.socket.emit('leave', { room: room });
  }

  generateUniqueString() {
    const array = new Uint8Array(24);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => ('0' + byte.toString(16)).slice(-2))
      .join('')
      .slice(0, 24);
  }

  on(event, callback) {
    this.socket.on(event, (...args) => callback(...args));
  }

  /**
   * Registers a callback to be executed when data is received for the specified event.
   * @param {string} event - The event name.
   * @param {function} callback - The callback function to execute when data is received.
   */
  onData(event, callback) {
    this.socket.on(event, (...args) => callback(...args));
  }

  // Method to start the connection
  connect() {
    this.socket.connect();
  }

  /**
   * Checks if the client is connected to the server.
   * @returns {boolean} - True if connected, false otherwise.
   */
  isConnected() {
    return this.socket.connected;
  }

  /**
   * Disconnects the client from the Socket.IO server.
   */
  disconnect() {
    this.socket.disconnect();
  }
}

export default SocketIOClient;
